import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import logo from '../images/isuzulyallpur1.png'; // adjust path
import SearchModal from "./search";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // close mobile menu on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // close mobile menu when navigating
  const handleNavigationClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="relative h-16 flex items-center">
          {/* --- MOBILE: centered logo trick --- */}
          <div className="absolute left-1/2 transform -translate-x-1/2 md:static md:transform-none md:mr-0">
            <Link to="/" onClick={handleNavigationClick} className="inline-block">
              <img
                src={logo}
                alt="Isuzu Lyallpur"
                className="h-10 w-auto animate-logo-fade"
                style={{ willChange: 'transform, opacity' }}
              />
            </Link>
          </div>

          {/* Desktop menu (right side) */}
          <div className="hidden md:flex items-center ml-auto space-x-6">
            <NavLink to="/" label="Home" activePath={location.pathname} onClick={handleNavigationClick} />
            <NavLink to="/about" label="About Us" activePath={location.pathname} onClick={handleNavigationClick} />
            <NavLink to="/sales" label="Sales" activePath={location.pathname} onClick={handleNavigationClick} />
            <NavLink to="/service" label="Service" activePath={location.pathname} onClick={handleNavigationClick} />
            <NavLink to="/parts" label="Parts" activePath={location.pathname} onClick={handleNavigationClick} />
            <NavLink to="/customer" label="Customer Relations" activePath={location.pathname} onClick={handleNavigationClick} />
            <NavLink to="/contact" label="Contact Us" activePath={location.pathname} onClick={handleNavigationClick} />
            <NavLink to="/faq" label="FAQ" activePath={location.pathname} onClick={handleNavigationClick} />
            <NavLink to="/videos" label="Videos" activePath={location.pathname} onClick={handleNavigationClick} />

            {/* Videos icon */}
            {/* <Link
              to="/videos"
              onClick={handleNavigationClick}
              className="ml-2 inline-flex items-center justify-center p-2 rounded-full transition-all hover:bg-gray-100"
              title="Videos"
            >
              <Video size={20} className="text-gray-800 hover:text-[#E62E2D]" />
            </Link> */}
            <SearchModal />
          </div>

          {/* Mobile hamburger (right side) */}
          <div className="flex items-center justify-between w-full md:hidden px-2">
            {/* 🍔 Hamburger on Left */}
            <button
              onClick={() => setIsMobileMenuOpen((s) => !s)}
              className="text-gray-800 hover:text-[#E62E2D] focus:outline-none p-2"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* 🔍 Search on Right */}
            <div className="p-2">
              <SearchModal />
            </div>
          </div>
        </div>
      </div>

      {/* =========================
            Mobile Drawer (right)
         ========================= */}
      {/* Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          aria-hidden="true"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </div>
      )}

      {/* Drawer panel */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-[82%] max-w-sm bg-white shadow-2xl transform transition-transform duration-400 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        role="dialog"
        aria-modal="true"
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
          <div className="flex-1 text-center">
            <Link to="/" onClick={handleNavigationClick}>
              <img src={logo} alt="Isuzu Lyallpur" className="h-9 inline-block animate-logo-fade" />
            </Link>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 rounded-md hover:bg-gray-100 ml-2"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Drawer content */}
        <div className="p-4 space-y-2 overflow-y-auto h-[calc(100%-72px)]">
          <MobileNavLink to="/" label="Home" onClick={handleNavigationClick} activePath={location.pathname} />
          <MobileNavLink to="/about" label="About Us" onClick={handleNavigationClick} activePath={location.pathname} />
          <MobileNavLink to="/sales" label="Sales" onClick={handleNavigationClick} activePath={location.pathname} />
          <MobileNavLink to="/service" label="Service" onClick={handleNavigationClick} activePath={location.pathname} />
          <MobileNavLink to="/parts" label="Parts" onClick={handleNavigationClick} activePath={location.pathname} />
          <MobileNavLink to="/customer" label="Customer Relations" onClick={handleNavigationClick} activePath={location.pathname} />
          <MobileNavLink to="/contact" label="Contact Us" onClick={handleNavigationClick} activePath={location.pathname} />
          <MobileNavLink to="/faq" label="FAQ" onClick={handleNavigationClick} activePath={location.pathname} />

          <Link
            to="/videos"
            onClick={handleNavigationClick}
            className="flex items-center space-x-3 px-3 py-3 rounded-md hover:bg-gray-50 transition"
          >
            {/* <Video size={18} /> */}
            <span className="text-base font-medium">Videos</span>
          </Link>
        </div>

        {/* Decorative bottom handle */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-3">
          <div className="h-1 w-12 rounded-full bg-gray-200" />
        </div>
      </div>

      {/* Custom animations + underline tweaks */}
      <style>{`
        @keyframes logoFade {
          0% { opacity: 0; transform: scale(0.8) translateY(-4px); }
          50% { opacity: 1; transform: scale(1.08) translateY(0); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-logo-fade { animation: logoFade 900ms cubic-bezier(.2,.9,.3,1); }

        .underline-anim {
          position: relative;
          display: inline-block;
        }
        .underline-anim::before {
          content: '';
          position: absolute;
          left: 0;
          bottom: -2px;
          height: 2px;
          width: 0%;
          background: #E62E2D;
          border-radius: 1px;
          transition: width 250ms ease;
        }
        .underline-anim:hover::before,
        .underline-anim.active::before {
          width: 100%;
        }
        .duration-400 { transition-duration: 400ms; }
      `}</style>
    </nav>
  );
};

/* -------------------- small components -------------------- */

const NavLink = ({ to, label, onClick, activePath }) => {
  const isActive = activePath === to || (to !== '/' && activePath.startsWith(to));
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`px-2 py-2 text-base font-medium text-gray-800 transition transform duration-150 hover:-translate-y-0.5 ${isActive ? 'text-[#E62E2D]' : ''}`}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className={`underline-anim ${isActive ? 'active' : ''}`}>{label}</span>
    </Link>
  );
};

const MobileNavLink = ({ to, label, onClick, activePath }) => {
  const isActive = activePath === to || (to !== '/' && activePath.startsWith(to));
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block px-3 py-3 rounded-md text-base font-medium transition-colors duration-150 ${isActive ? 'text-[#E62E2D] bg-gray-50' : 'text-gray-800 hover:bg-gray-50'}`}
    >
      {label}
    </Link>
  );
};

export default Navbar;
